import React, { useEffect, useState } from 'react';
import { AuditLogEventName, StyledDivider } from 'pages/AuditLogPage/AuditLogViewPage/styles';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTranslations } from 'hooks/useTranslations';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { useNavigate } from 'react-router-dom';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { ERouteLinks } from 'models/route';
import { IAuditLogItemResponse } from 'models/auditLog';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import { EMIMETypesLabel } from 'constants/MIMETypes';
import PDFViewer from 'components/molecules/PDFViewer';

export interface ILicencesAuthorityObject {
  uniqueId?: string;
  dictionaryUniqueId?: string;
  name?: string;
  isActive?: string;
  authorizedPersonName?: string;
  position?: string;
  signatureAttachmentUniqueId?: string;
  signatureAttachmentUrl?: string;
  signatureAttachmentType?: string;
  signatureAttachmentName?: string;
}

interface ILegalEntityLicencesAuthorityEventDetails {
  auditLog: IAuditLogItemResponse;
}

interface IRenderDetails {
  authorityDetail: ILicencesAuthorityObject;
  t: any;
}

const RenderDetails = ({ authorityDetail, t }: IRenderDetails) => {
  return (
    <>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licenceAuthorities.licenceAuthorityName.label')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(authorityDetail?.name)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licenceAuthorities.status.label')}
        </Typography>
        <Typography variant="body2">
          <Chips
            text={
              authorityDetail.isActive
                ? t('general.active.label')
                : t('general.inactive.label')
            }
            variant={authorityDetail.isActive ? EChipsVariants.success : EChipsVariants.error}
            fullWidth
          />
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licenceAuthorities.authorizedPersonName.label')}
        </Typography>
        <Typography variant="body2">
          {emptyFieldHolder(authorityDetail?.authorizedPersonName)}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licenceAuthorities.position.label')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(authorityDetail?.position)}</Typography>
      </Grid>
      {authorityDetail?.signatureAttachmentUrl && (
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
            {t('licenceAuthorities.licenceAuthoritySignaturePreview.label')}
          </Typography>
          <Typography variant="body2">
            {authorityDetail?.signatureAttachmentType === EMIMETypesLabel.PDF ? (
              <PDFViewer pdfUrl={authorityDetail?.signatureAttachmentUrl ?? ''} height={320} />
            ) : (
              <img
                src={authorityDetail?.signatureAttachmentUrl}
                alt="file"
                style={{
                  width: 'auto',
                  maxWidth: '100%',
                  maxHeight: '320px',
                  objectFit: 'cover',
                }}
              />
            )}
          </Typography>
        </Grid>
      )}
    </>
  );
};

const LegalEntityLicencesAuthorityEventDetails = ({
  auditLog,
}: ILegalEntityLicencesAuthorityEventDetails) => {
  const [authorityDetails, setLicencesAuthorityDetails] =
    useState<ILicencesAuthorityObject | null>(null);
  const [previousLicencesAuthorityDetails, setPreviousLicencesAuthorityDetails] =
    useState<ILicencesAuthorityObject | null>(null);
  const { t } = useTranslations();
  const navigate = useNavigate();

  useEffect(() => {
    if (auditLog?.postObjectValue) {
      setLicencesAuthorityDetails(
        auditLog.postObjectValue as unknown as ILicencesAuthorityObject,
      );
    } else {
      setLicencesAuthorityDetails(null);
    }

    if (auditLog?.previousObjectValue) {
      setPreviousLicencesAuthorityDetails(
        auditLog.previousObjectValue as unknown as ILicencesAuthorityObject,
      );
    } else {
      setPreviousLicencesAuthorityDetails(null);
    }
  }, [auditLog]);

  const handleRedirect = (details?: ILicencesAuthorityObject | null) => {
    if (!details?.uniqueId) return;

    navigate(`${ERouteLinks.DetailsLicenceAuthorities.replace(':id', details.uniqueId)}`);
  };

  return (
    <>
      {previousLicencesAuthorityDetails && (
        <>
          <AuditLogEventName>
            <Typography variant="h5">
              {`${t('licences.licencingAuthority.label')} ${t('auditLogDetails.before.event.title')}`}
            </Typography>
          </AuditLogEventName>
          <DetailsTemplate
            title={previousLicencesAuthorityDetails?.name || ''}
            pageType={EDetailsPageTypes.auditLogView}
            redirectToView={() => handleRedirect(previousLicencesAuthorityDetails)}
          >
            <RenderDetails authorityDetail={previousLicencesAuthorityDetails} t={t} />
          </DetailsTemplate>
        </>
      )}

      {authorityDetails && (
        <>
          <AuditLogEventName
            sx={{ marginTop: previousLicencesAuthorityDetails ? '20px' : '0' }}
          >
            <Typography variant="h5">
              {`${t('licences.licencingAuthority.label')} ${t(
                previousLicencesAuthorityDetails
                  ? 'auditLogDetails.after.event.title'
                  : 'auditLogDetails.post.event.title',
              )}`}
            </Typography>
          </AuditLogEventName>
          <DetailsTemplate
            title={authorityDetails?.name || ''}
            pageType={EDetailsPageTypes.auditLogView}
            redirectToView={() => handleRedirect(authorityDetails)}
          >
            <RenderDetails authorityDetail={authorityDetails} t={t} />
          </DetailsTemplate>
        </>
      )}
    </>
  );
};

export default LegalEntityLicencesAuthorityEventDetails;
